import React, { useState, useRef } from 'react';
import { postsApi } from '../services/api';
import { useNavigate } from 'react-router-dom';
import Modal, { useModal } from '../components/Modal';
import { useAuth } from '../context/AuthContext';
import LoadingOverlay from '../components/LoadingOverlay';

export default function CreatePostPage() {
  const navigate = useNavigate();
  const { modal, show, close } = useModal();
  const [caption, setCaption] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [mediaType, setMediaType] = useState<'image' | 'video'>('image');
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  // Si propone solo ciò che è davvero collegato: chi ha la sola Pagina Facebook
  // non deve vedersi rifiutare il post per "Instagram non collegato".
  const hasFb = !!user?.has_facebook;
  const hasIg = !!user?.has_instagram;
  const [pubFb, setPubFb] = useState(hasFb);
  const [pubIg, setPubIg] = useState(hasIg);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) {
      setFile(f);
      setPreview(URL.createObjectURL(f));
      setMediaType(f.type.startsWith('video') ? 'video' : 'image');
    }
  };

  const switchType = (t: 'image' | 'video') => {
    setMediaType(t); setFile(null); setPreview(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      show({ title: 'File mancante', message: 'Seleziona un file da pubblicare.', type: 'warning', onConfirm: close });
      return;
    }
    if (!caption.trim()) {
      show({ title: 'Testo mancante', message: 'Inserisci una didascalia per il post.', type: 'warning', onConfirm: close });
      return;
    }
    if (!pubFb && !pubIg) {
      show({ title: 'Piattaforma mancante', message: 'Seleziona almeno una piattaforma di pubblicazione.', type: 'warning', onConfirm: close });
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('caption', caption);
      fd.append('media_type', mediaType);
      fd.append('media', file);
      fd.append('publish_facebook', pubFb ? '1' : '0');
      fd.append('publish_instagram', pubIg ? '1' : '0');
      await postsApi.create(fd);
      setLoading(false);
      show({
        title: 'Post inviato!',
        message: 'Il tuo contenuto è stato ricevuto. Tra qualche momento lo vedrai pubblicato su Facebook e Instagram.',
        type: 'success',
        confirmText: 'Perfetto!',
        onConfirm: () => { close(); navigate('/posts'); },
      });
    } catch (err: any) {
      setLoading(false);
      show({
        title: 'Errore pubblicazione',
        message: err.response?.data?.error || 'Si è verificato un errore durante la pubblicazione.',
        type: 'error',
        onConfirm: close,
      });
    }
  };

  return (
    <div className="page">
      {loading && <LoadingOverlay title="Pubblicazione in corso..." subtitle="Carichiamo il contenuto e lo inviamo ai tuoi social" icon="cloud_upload" />}
      {modal && (
        <Modal
          isOpen
          title={modal.title}
          message={modal.message}
          type={modal.type}
          confirmText={modal.confirmText}
          cancelText={modal.cancelText}
          onConfirm={modal.onConfirm}
          onCancel={modal.onCancel ?? close}
        />
      )}

      <div className="top-nav">
        <button className="back-btn" onClick={() => navigate(-1)}>
          <span className="mi mi-20">arrow_back</span> Indietro
        </button>
        <h1 className="nav-title">Nuovo contenuto</h1>
      </div>

      <p className="sub" style={{ textAlign: 'center', marginBottom: 4 }}>Pubblica su Facebook e Instagram contemporaneamente</p>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

        {/* Tipo media */}
        <div className="media-toggle">
          <button type="button" className={`media-toggle-btn${mediaType === 'image' ? ' active' : ''}`} onClick={() => switchType('image')}>
            <span className="mi mi-18">image</span> Immagine
          </button>
          <button type="button" className={`media-toggle-btn${mediaType === 'video' ? ' active' : ''}`} onClick={() => switchType('video')}>
            <span className="mi mi-18">videocam</span> Video
          </button>
        </div>

        {/* Upload */}
        <div className="upload-area" onClick={() => fileRef.current?.click()} style={preview ? { border: '2px dashed rgba(79,114,255,0.4)', minHeight: 180 } : {}}>
          {preview ? (
            mediaType === 'video'
              ? <video src={preview} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted />
              : <img src={preview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <>
              <span className="mi" style={{ fontSize: 36, color: 'var(--text-3)' }}>
                {mediaType === 'video' ? 'videocam' : 'add_photo_alternate'}
              </span>
              <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-2)' }}>
                Seleziona {mediaType === 'video' ? 'video' : 'immagine'}
              </span>
              <span style={{ fontSize: 12, color: 'var(--text-3)' }}>
                {mediaType === 'video' ? 'MP4, MOV — max 100MB' : 'JPG, PNG — max 50MB'}
              </span>
            </>
          )}
        </div>
        {file && (
          <p style={{ textAlign: 'center', color: 'var(--primary)', cursor: 'pointer', fontSize: 13, marginTop: -8 }}
             onClick={() => fileRef.current?.click()}>
            Cambia {mediaType === 'video' ? 'video' : 'immagine'}
          </p>
        )}

        <input ref={fileRef} type="file" accept={mediaType === 'video' ? 'video/*' : 'image/*'}
          onChange={handleFile} style={{ display: 'none' }} />

        {/* Didascalia */}
        <div className="form-group">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <label className="form-label">Didascalia</label>
            <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{caption.length} / 2200</span>
          </div>
          <textarea className="form-input" rows={4} value={caption}
            onChange={e => setCaption(e.target.value)}
            placeholder="Scrivi il testo del post..."
            maxLength={2200} />
        </div>

        {/* Piattaforme */}
        <div className="form-group">
          <label className="form-label">Pubblica su</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {hasFb && <button
              type="button"
              onClick={() => setPubFb(v => !v)}
              style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                padding: '11px 14px', borderRadius: 10, cursor: 'pointer',
                border: `1.5px solid ${pubFb ? '#1877F2' : 'var(--border-md)'}`,
                background: pubFb ? 'rgba(24,119,242,0.12)' : 'var(--s2)',
                color: pubFb ? '#5B9CF6' : 'var(--text-3)',
                fontWeight: 600, fontSize: 13, fontFamily: 'inherit',
              }}
            >
              <span style={{ fontWeight: 900, fontSize: 16 }}>f</span> Facebook
              {pubFb && <span className="mi mi-16" style={{ color: '#5B9CF6' }}>check_circle</span>}
            </button>}
            {hasIg && <button
              type="button"
              onClick={() => setPubIg(v => !v)}
              style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                padding: '11px 14px', borderRadius: 10, cursor: 'pointer',
                border: `1.5px solid ${pubIg ? '#E1306C' : 'var(--border-md)'}`,
                background: pubIg ? 'rgba(225,48,108,0.10)' : 'var(--s2)',
                color: pubIg ? '#E1306C' : 'var(--text-3)',
                fontWeight: 600, fontSize: 13, fontFamily: 'inherit',
              }}
            >
              <span className="mi mi-16">photo_camera</span> Instagram
              {pubIg && <span className="mi mi-16" style={{ color: '#E1306C' }}>check_circle</span>}
            </button>}
          </div>
          {!pubFb && !pubIg && (
            <p style={{ fontSize: 11, color: 'var(--error)', marginTop: 4 }}>Seleziona almeno una piattaforma</p>
          )}
        </div>

        <button type="submit" className="btn btn-primary" disabled={loading || !file || !caption.trim()}>
          <span className="mi mi-20">publish</span>
          Pubblica
        </button>
      </form>
    </div>
  );
}
